/**
 * What a shopper may be shown of a product, in the BROWSER, after a live
 * snapshot is merged into the card the server rendered.
 *
 * The server already presented the card (present-product.ts) and stamped what
 * the card identity guard refused (`refusedListings`, `refusedImages`). Here
 * the same pipeline (present-product-core.ts) runs with those stamps as its
 * judge, plus the ones use-live-products.ts adds when it judges what only a
 * snapshot brings: a snapshot that brings a refused listing or photo back is
 * refused again, and the guard itself (with its brand lists) never ships to
 * the browser.
 *
 * Client-safe: imports neither card-identity.ts nor present-product.ts.
 */

import type { ImageType, Product } from "../types/product.type";
import { type IdentityJudge, listingKey, presentProductWith } from "./present-product-core";

/** The identity guard's refusals, read from the stamps. */
const STAMPS: IdentityJudge = {
  listingIsAnotherProduct: (product) => {
    const key = listingKey(product.externalLinks?.href);
    return !!key && (product.refusedListings ?? []).includes(key);
  },
  imageIsAnotherProduct: (image: ImageType, product) =>
    typeof image?.src === "string" && (product.refusedImages ?? []).includes(image.src),
};

/** What a live snapshot brought, as far as the guard is concerned. */
export type LiveIdentityFields = {
  images?: unknown;
  externalLinks?: { images?: unknown[] } | null;
  refusedListings?: string[];
  refusedImages?: string[];
};

function union(a: readonly string[] | undefined, b: readonly string[] | undefined): string[] | undefined {
  if (!b?.length) return a ? [...a] : undefined;
  return [...new Set([...(a ?? []), ...b])];
}

/**
 * The card as shown: `product` is the server's card with the live snapshot's
 * fields already merged, `live` the snapshot (its refusals, and whether it
 * brought photos). A snapshot that brings photos re-decides the neutral tile
 * (`imageWithheld`): kept when the photos are refused again, dropped when a
 * photo the guard accepts arrives.
 */
export function presentLiveProduct<T extends Product>(
  product: T,
  regionCode: string | null | undefined,
  live?: LiveIdentityFields | null,
): T {
  if (!product) return product;
  let shown = product;
  if (live) {
    const refusedListings = union(product.refusedListings, live.refusedListings);
    const refusedImages = union(product.refusedImages, live.refusedImages);
    const bringsPhotos = live.images !== undefined || (live.externalLinks?.images?.length ?? 0) > 0;
    const edit = { ...product } as T;
    if (refusedListings?.length) edit.refusedListings = refusedListings;
    if (refusedImages?.length) edit.refusedImages = refusedImages;
    if (bringsPhotos) delete (edit as { imageWithheld?: unknown }).imageWithheld;
    shown = edit;
  }
  return presentProductWith(shown, regionCode, STAMPS);
}
